import * as React from 'react';
import { Box, Typography, CircularProgress, IconButton, Dialog } from '@mui/material';
import { useGetIdentity, SimpleForm, useGetOne, useDataProvider } from 'react-admin';
import { UserFields } from '../../../shared/constants/users';
import { Resources } from '../../../shared/constants/resources';
import CommonImageInput from '../../../components/inputs/CommonImageInput';
import { Clear } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useFormContext } from 'react-hook-form';
import { stringToColour } from '../../../shared/utils/stringToColour';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from 'react-query';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { firebaseApp } from '../../../auth/FirebaseAuth';

const FormBody = ({ handleUpdateAvatar, isLoading }) => {
  const theme = useTheme();
  const { watch, setValue } = useFormContext();
  const values = watch();
  const [avatarUrl, setAvatarUrl] = React.useState('');
  
  React.useEffect(() => {
    const fetchImage = async () => {
      if (values[UserFields.ProfilePicture]) {
        try {
          const url = await getDownloadURL(
            ref(getStorage(), values[UserFields.ProfilePicture])
          );
          setAvatarUrl(url);
        } catch (error) {
          console.error('Error fetching avatar:', error);
        }
      }
    };

    fetchImage();
  }, [values[UserFields.ProfilePicture]]);

  const photoDefaultBackground =
    values[UserFields.FirstName] && values[UserFields.LastName]
      ? stringToColour(
          `${values[UserFields.FirstName]} ${values[UserFields.LastName]}`,
          60
        )
      : '';
  const photoDefaultText = photoDefaultBackground && (
    <Typography
      variant="h1"
      sx={{ color: theme.palette.getContrastText(photoDefaultBackground), fontSize: 40 }}
    >
      {values[UserFields.FirstName]?.[0] || ''}
      {values[UserFields.LastName]?.[0] || ''}
    </Typography>
  );

  return (
    <CommonImageInput
      source={UserFields.ProfilePicture}
      // valueSource={UserFields.PhotoPath}
      value={avatarUrl}
      label=" "
      inputText={photoDefaultText}
      inputBackground={avatarUrl || photoDefaultBackground}
      ignorePolicies
      accept="image/png, image/jpeg"
      isLoading={isLoading}
      onDelete={() => {
        setAvatarUrl('');
        handleUpdateAvatar('delete');
      }}
      onChange={(croppedFile) =>
        handleUpdateAvatar('save', {
          ...values,
          [UserFields.ProfilePicture]: croppedFile,
        })
      }
    />
  );
};

const UpdateAvatar = ({ open, handleCloseModal }) => {
  const sx = {
    root: {
      px: 4,
      py: 2,
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  };
  const dp = useDataProvider();
  const {
    identity,
    isLoading: isIdentityLoading,
    refetch: refetchIdentity,
  } = useGetIdentity();
  const queryClient = useQueryClient();

  const updateAvatarMutation = useMutation([Resources.Users], async (updateData: any) => {
    return dp.update(Resources.Users, {
      id: identity.id,
      data: updateData,
      previousData: identity,
    });
  });

  const handleUpdateAvatar = async (action, values = null) => {
    try {
      const storage = getStorage(firebaseApp);
      let updateData = {};

      if (action === 'delete') {
        // Delete the old avatar from Firebase Storage if it exists
        if (identity[UserFields.ProfilePicture]) {
          try {
            const oldAvatarRef = ref(storage, identity[UserFields.ProfilePicture]);
            await deleteObject(oldAvatarRef);
          } catch (error) {
            console.error('Error deleting old avatar:', error);
            // Continue with the update even if deletion fails
          }
        }
        updateData = { [UserFields.ProfilePicture]: null };
      } else {
        // Upload the new avatar to Firebase Storage
        const file = values[UserFields.ProfilePicture].rawFile;
        const fileExtension = file.name.split('.').pop();
        const avatarPath = `profile_pictures/${
          identity.id
        }_${Date.now()}.${fileExtension}`;
        const avatarRef = ref(storage, avatarPath);

        // Upload the file
        await uploadBytes(avatarRef, file);

        // Delete the old avatar if it exists
        if (identity[UserFields.ProfilePicture]) {
          try {
            const oldAvatarRef = ref(storage, identity[UserFields.ProfilePicture]);
            await deleteObject(oldAvatarRef);
          } catch (error) {
            console.error('Error deleting old avatar:', error);
            // Continue with the update even if deletion fails
          }
        }

        updateData = {
          [UserFields.ProfilePicture]: avatarPath,
        };
      }

      await updateAvatarMutation.mutateAsync(updateData);
      enqueueSnackbar(
        `Avatar ${action === 'delete' ? 'deleted' : 'updated'} successfully`,
        {
          variant: 'success',
        }
      );
      refetchIdentity();
      queryClient.invalidateQueries(Resources.Profiles);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'warning' });
    }
  };

  if (isIdentityLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={handleCloseModal}>
      <Box sx={sx.root}>
        <Box sx={sx.header}>
          <Typography variant="h4" component="h2">
            Update user avatar
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <Clear sx={{ fontSize: 40, color: 'common.black' }} />
          </IconButton>
        </Box>
        <SimpleForm
          sx={{ mt: 2 }}
          record={identity}
          onSubmit={(values) => handleUpdateAvatar('save', values)}
          toolbar={false}
        >
          <FormBody
            handleUpdateAvatar={handleUpdateAvatar}
            isLoading={isIdentityLoading || updateAvatarMutation.isLoading}
          />
        </SimpleForm>
      </Box>
    </Dialog>
  );
};

export default UpdateAvatar;
